import { Box, Center, HStack, Text } from "@chakra-ui/layout";
import { Button, Collapse, IconButton } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  Card,
  LoadingOverlay,
  ModalConfirm,
  ModuleTitle,
} from "@src/components/ui-kit";
import { EditableText } from "@src/components/ui-kit/editable-text";
import { Icon } from "@src/components/ui-kit/Icon";
import { TaskItemProject } from "@src/components/ui-kit/TaskItem/TaskItemProject";
import { useStore } from "@src/utils/hooks";
import { FieldState } from "formstate";
import { observer, useLocalObservable } from "mobx-react-lite";
import type { TemplatesNavigationProps } from "../navigation";
import { TaskGroupTemplateModal } from "./modal";
import { TaskGroupTemplateCreateModalStore } from "./modal/store";
import { TaskGroupTemplatesStore } from "./store";

type TaskGroupTemplatesPageProps = {
  template: TemplatesNavigationProps["currentTemplate"];
};
export const TaskGroupTemplatesPage = observer(function TaskGroupTemplatesPage({
  template,
}: TaskGroupTemplatesPageProps) {
  const appStore = useStore();
  const { store } = useLocalObservable(() => ({
    store: new TaskGroupTemplatesStore(
      appStore,
      new TaskGroupTemplateCreateModalStore(appStore),
    ),
  }));

  return (
    <Box>
      <HStack justifyContent="space-between" mb="4">
        <ModuleTitle>
          <Text fontWeight="inherit">{template?.title()}</Text>
        </ModuleTitle>
        {store.taskGroupTemplates.length > 0 && (
          <Button
            leftIcon={<Icon name="plus" />}
            onClick={store.handleCreateTemplate}
          >
            <Trans>New template</Trans>
          </Button>
        )}
      </HStack>
      <LoadingOverlay isLoading={store.fetcher.isLoading.value}>
        {store.taskGroupTemplates.length > 0 ? (
          store.taskGroupTemplates.map((template) => (
            <Card
              key={template.id}
              bgColor="teal.50"
              borderColor="gray.300"
              p="4"
              _notLast={{
                mb: 2,
              }}
            >
              <HStack justifyContent="space-between" mb="2">
                <HStack spacing="2">
                  <IconButton
                    aria-label={t`Uncollapse button`}
                    colorScheme="teal"
                    icon={
                      <Icon
                        name="chevron-down"
                        transform={`rotate(${
                          store.collapsedTaskGroups.has(template.id)
                            ? "0deg"
                            : "180deg"
                        })`}
                        transition="transform 200ms ease, top 200ms ease"
                      />
                    }
                    onClick={() => {
                      store.handleCollapseGroup(template.id);
                    }}
                    variant="subtle"
                  />
                  <EditableText
                    field={new FieldState(template.name)}
                    isLoading={store.updateMutator.isLoading.value}
                    onConfirm={async (value) => {
                      await store.updateTemplateGroup(template.id, value);
                    }}
                    inputStyles={{
                      w: "300px",
                    }}
                    textStyles={{
                      fontSize: "md",
                      fontWeight: "600",
                    }}
                  />
                  <Button
                    colorScheme="purple"
                    onClick={() => {
                      store.handleAddTask(template.id);
                    }}
                    size="xs"
                    variant="outline"
                  >
                    <Trans>New task</Trans>
                  </Button>
                </HStack>
                <IconButton
                  aria-label={t`Task group template delete button`}
                  colorScheme="red"
                  icon={<Icon name="trash-03" />}
                  onClick={() => store.handleSetGroupToDelete(template.id)}
                  variant="subtle"
                />
              </HStack>
              <Collapse
                animateOpacity
                in={!store.collapsedTaskGroups.has(template.id)}
              >
                {template.tasks?.map((task) => (
                  <TaskItemProject
                    key={`template-task-${task.id}`}
                    task={task}
                    onCreateSubtask={store.handleOnCreateSubtask}
                    onClick={(task) => {
                      store.handleTaskClick(task.id);
                    }}
                  />
                ))}
              </Collapse>
            </Card>
          ))
        ) : (
          <Center flexDirection="column" h="55dvh">
            <Text fontSize="2xl">
              <strong>
                <Trans>No templates created yet</Trans>
              </strong>
            </Text>
            <Button onClick={store.handleCreateTemplate}>
              <Trans>Create one now</Trans>
            </Button>
          </Center>
        )}
      </LoadingOverlay>
      <TaskGroupTemplateModal store={store.modalStore} />
      <ModalConfirm
        isOpen={store.isDeleteModalOpen}
        onClose={() => {
          store.handleSetGroupToDelete(null);
        }}
        onConfirm={store.deleteTemplateGroup}
        isLoading={store.deleteMutator.isLoading.value}
        heading={t`Delete task group template`}
        destructive
      >
        <Trans>Are you sure you want to delete this task group template?</Trans>
      </ModalConfirm>
    </Box>
  );
});
